import { useState, useCallback, useMemo, useEffect } from "react"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import {
  DTAB_STATISTIC_OPTIONS,
  XTAB_STATISTIC_OPTIONS,
} from "features/Reporting/components/ReportingDisplay/Elements/shared/StatisticTypeSelectOptions"

/**
 * Configuration for operator management
 */
export interface OperatorManagementConfig {
  /** Initial operation type */
  initialOperationType?: TABLE_OPERATION_TYPE
  /** Initial selected operators */
  initialOperators?: STATISTIC_TYPE[]
  /** Initial single statistic type (for chart elements) */
  initialStatisticType?: STATISTIC_TYPE
  /** Callback when operation type changes */
  onOperationTypeChange?: (operationType: TABLE_OPERATION_TYPE) => void
  /** Callback when operators change */
  onOperatorsChange?: (operators: STATISTIC_TYPE[]) => void
  /** Callback when single statistic type changes */
  onStatisticTypeChange?: (statisticType: STATISTIC_TYPE) => void
  /** Whether to use single statistic mode (for charts) */
  singleStatisticMode?: boolean
}

/**
 * Table operation type options
 */
export const TABLE_OPERATION_OPTIONS = [
  {
    value: TABLE_OPERATION_TYPE.XTAB,
    label: "Cross-tabulation (XTAB)",
    description: "Cross-tabulation for categorical data analysis",
  },
  {
    value: TABLE_OPERATION_TYPE.DTAB,
    label: "Data-tabulation (DTAB)",
    description: "Data-tabulation for numerical data analysis",
  },
]

/**
 * Default operators for different operation types
 */
const DEFAULT_XTAB_OPERATORS = [
  STATISTIC_TYPE.N,
  STATISTIC_TYPE.BIG_N,
  STATISTIC_TYPE.ROW_PERCENT,
  STATISTIC_TYPE.COL_PERCENT,
  STATISTIC_TYPE.INDEX,
  STATISTIC_TYPE.PERC_PERCENT,
  STATISTIC_TYPE.AVG,
]

const DEFAULT_DTAB_OPERATORS = [
  STATISTIC_TYPE.N,
  STATISTIC_TYPE.AVG,
  STATISTIC_TYPE.BASE_N,
]

/**
 * Hook for managing operator selection and operation type changes
 * Centralizes all logic related to DTAB vs XTAB statistic options
 */
export const useOperatorManagement = (
  config: OperatorManagementConfig = {}
) => {
  const {
    initialOperationType = TABLE_OPERATION_TYPE.XTAB,
    initialOperators,
    initialStatisticType = STATISTIC_TYPE.N,
    onOperationTypeChange,
    onOperatorsChange,
    onStatisticTypeChange,
    singleStatisticMode = false,
  } = config

  // State management
  const [operationType, setOperationType] =
    useState<TABLE_OPERATION_TYPE>(initialOperationType)
  const [selectedOperators, setSelectedOperators] = useState<STATISTIC_TYPE[]>(
    initialOperators ||
      (initialOperationType === TABLE_OPERATION_TYPE.DTAB
        ? DEFAULT_DTAB_OPERATORS
        : DEFAULT_XTAB_OPERATORS)
  )
  const [selectedStatisticType, setSelectedStatisticType] =
    useState<STATISTIC_TYPE>(initialStatisticType)

  // Get available statistic options based on current operation type
  const availableStatisticOptions = useMemo(() => {
    return operationType === TABLE_OPERATION_TYPE.DTAB
      ? DTAB_STATISTIC_OPTIONS
      : XTAB_STATISTIC_OPTIONS
  }, [operationType])

  // Get available statistic values for validation
  const availableStatisticValues = useMemo(() => {
    return availableStatisticOptions.map((option) => option.value)
  }, [availableStatisticOptions])

  // Validate and update operators when operation type changes
  const validateAndUpdateOperators = useCallback(
    (
      newOperationType: TABLE_OPERATION_TYPE,
      currentOperators: STATISTIC_TYPE[]
    ) => {
      const newAvailableValues =
        newOperationType === TABLE_OPERATION_TYPE.DTAB
          ? DTAB_STATISTIC_OPTIONS.map((opt) => opt.value)
          : XTAB_STATISTIC_OPTIONS.map((opt) => opt.value)

      // Filter out invalid operators
      const validOperators = currentOperators.filter((op) =>
        newAvailableValues.includes(op)
      )

      // If no valid operators remain, use defaults
      if (validOperators.length === 0) {
        return newOperationType === TABLE_OPERATION_TYPE.DTAB
          ? DEFAULT_DTAB_OPERATORS
          : DEFAULT_XTAB_OPERATORS
      }

      return validOperators
    },
    []
  )

  // Validate and update single statistic type
  const validateAndUpdateStatisticType = useCallback(
    (
      newOperationType: TABLE_OPERATION_TYPE,
      currentStatisticType: STATISTIC_TYPE
    ) => {
      const newAvailableValues =
        newOperationType === TABLE_OPERATION_TYPE.DTAB
          ? DTAB_STATISTIC_OPTIONS.map((opt) => opt.value)
          : XTAB_STATISTIC_OPTIONS.map((opt) => opt.value)

      // If current statistic is valid for new operation type, keep it
      if (newAvailableValues.includes(currentStatisticType)) {
        return currentStatisticType
      }

      // Otherwise, use the first available option
      return newAvailableValues[0] || STATISTIC_TYPE.N
    },
    []
  )

  // Handle operation type change
  const handleOperationTypeChange = useCallback(
    (newOperationType: TABLE_OPERATION_TYPE) => {
      setOperationType(newOperationType)

      if (singleStatisticMode) {
        // Update single statistic type
        const newStatisticType = validateAndUpdateStatisticType(
          newOperationType,
          selectedStatisticType
        )
        setSelectedStatisticType(newStatisticType)
        onStatisticTypeChange?.(newStatisticType)
      } else {
        // Update multiple operators
        const newOperators = validateAndUpdateOperators(
          newOperationType,
          selectedOperators
        )
        setSelectedOperators(newOperators)
        onOperatorsChange?.(newOperators)
      }

      onOperationTypeChange?.(newOperationType)
    },
    [
      selectedOperators,
      selectedStatisticType,
      singleStatisticMode,
      validateAndUpdateOperators,
      validateAndUpdateStatisticType,
      onOperationTypeChange,
      onOperatorsChange,
      onStatisticTypeChange,
    ]
  )

  // Handle operators change (for multi-select mode)
  const handleOperatorsChange = useCallback(
    (newOperators: STATISTIC_TYPE[]) => {
      setSelectedOperators(newOperators)
      onOperatorsChange?.(newOperators)
    },
    [onOperatorsChange]
  )

  // Handle single statistic type change (for single-select mode)
  const handleStatisticTypeChange = useCallback(
    (newStatisticType: STATISTIC_TYPE) => {
      setSelectedStatisticType(newStatisticType)
      onStatisticTypeChange?.(newStatisticType)
    },
    [onStatisticTypeChange]
  )

  // Check if current selection is valid
  const isCurrentSelectionValid = useMemo(() => {
    if (singleStatisticMode) {
      return availableStatisticValues.includes(selectedStatisticType)
    }
    return selectedOperators.every((op) =>
      availableStatisticValues.includes(op)
    )
  }, [
    singleStatisticMode,
    selectedStatisticType,
    selectedOperators,
    availableStatisticValues,
  ])

  // Get default operators for current operation type
  const getDefaultOperators = useCallback(
    (opType?: TABLE_OPERATION_TYPE) => {
      const targetType = opType || operationType
      return targetType === TABLE_OPERATION_TYPE.DTAB
        ? DEFAULT_DTAB_OPERATORS
        : DEFAULT_XTAB_OPERATORS
    },
    [operationType]
  )

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    const defaultOps = getDefaultOperators()
    setSelectedOperators(defaultOps)
    setSelectedStatisticType(STATISTIC_TYPE.N)

    if (singleStatisticMode) {
      onStatisticTypeChange?.(STATISTIC_TYPE.N)
    } else {
      onOperatorsChange?.(defaultOps)
    }
  }, [
    singleStatisticMode,
    getDefaultOperators,
    onOperatorsChange,
    onStatisticTypeChange,
  ])

  // Effect to handle external updates to initial values
  useEffect(() => {
    if (initialOperators && !singleStatisticMode) {
      setSelectedOperators(initialOperators)
    }
  }, [initialOperators, singleStatisticMode])

  useEffect(() => {
    if (initialStatisticType && singleStatisticMode) {
      setSelectedStatisticType(initialStatisticType)
    }
  }, [initialStatisticType, singleStatisticMode])

  return {
    // Current state
    operationType,
    selectedOperators,
    selectedStatisticType,

    // Available options
    availableStatisticOptions,
    availableStatisticValues,
    tableOperationOptions: TABLE_OPERATION_OPTIONS,

    // Handlers
    handleOperationTypeChange,
    handleOperatorsChange,
    handleStatisticTypeChange,

    // Validation
    isCurrentSelectionValid,
    validateAndUpdateOperators,
    validateAndUpdateStatisticType,

    // Utilities
    getDefaultOperators,
    resetToDefaults,

    // Mode info
    singleStatisticMode,
  }
}

export default useOperatorManagement
